export function pick(obj = {}, keys) {
  return Object.entries(obj)
    .filter(([key]) => keys.includes(key))
    .reduce((obj, [key, val]) => Object.assign(obj, { [key]: val }), {});
}

export function omit(obj = {}, keys) {
  return Object.entries(obj)
    .filter(([key]) => !keys.includes(key))
    .reduce((obj, [key, val]) => Object.assign(obj, { [key]: val }), {});
}

export async function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export async function timeoutPromise(ms, promise) {
  let intervalId, resolveTimeout;
  const timeout = new Promise((resolve, reject) => {
    resolveTimeout = resolve;
    intervalId = setTimeout(() => {
      clearTimeout(intervalId), intervalId = null;
      reject(new Error('Total report execution time limit exceeded'));
    }, ms);
  });

  const result = await Promise.race([promise, timeout]);
  intervalId && clearInterval(intervalId);
  resolveTimeout && resolveTimeout();
  return result;
}

export function reflect(promise) {
  return promise.then(data => ({ data, status: 'fulfilled' }), (error) => {
    console.warn('Reflect:', error.message);
    return { error, status: 'rejected' };
  });
}

export function areStringsInsensitivelyEqual(str1, str2) {
  return insensStringsComparator(str1, str2) === 0;
}

// TODO: remove after sync with merge production/excel365 => master, and master => production/portal
export function compareInsensitivelyStrings(str1, str2) {
  return areStringsInsensitivelyEqual(str1, str2);
}

// TODO: rename to 'compareInsensitivelyStrings' after merges (see above)
export function insensStringsComparator(str1, str2) {
  return typeof str1 === 'string' && typeof str2 === 'string'
    ? str1.localeCompare(str2, undefined, { sensitivity: 'accent' })
    : !(str1 === str2);
}

export function capitalizeString(value) {
  return value 
    ? value.charAt(0).toUpperCase() + value.slice(1)
    : '';
}

export function toCamelCase(str) {
  if (!str) { return ''; }
  const words = str.trim().split(/\s+/);
  const firstWord = words[0].toLowerCase();
  
  const camelCased = words.slice(1)
    .map(word => capitalizeString(word.toLowerCase()))
    .join('');
    
  return firstWord + camelCased;
}

export function toQueryString(data) {
  return Object.entries(data)
    .map(([key, value]) => `${key}=${value !== undefined ? value : ''}`)
    .join('&');
}

export function isPureObject(obj) {
  return null !== obj &&
    typeof obj === 'object' &&
    Object.getPrototypeOf(obj).isPrototypeOf(Object);
}

export function simpleDeepCopy(arrayOrObject) {
  return Array.isArray(arrayOrObject)
    ? arrayOrObject.map(x => simpleDeepCopy(x))
    : isPureObject(arrayOrObject)
      ? Object.entries(arrayOrObject).reduce((obj, [key, val]) =>
        Object.assign(obj, { [key]: simpleDeepCopy(val) })
        , {})
      : arrayOrObject;
}

export function lowercaseKeys(object) {
  const result = {};
  if (object && typeof object === 'object' && !Array.isArray(object)) {
    Object.keys(object).forEach(key => result[key.toLowerCase()] = object[key]);
  }
  return result;
}

export function findByInsensitiveKey(object, key) {
  if (!object || typeof object !== 'object') { return null }

  key = key.toLowerCase();
  const result = Object.keys(object).find(key1 => key1.toLowerCase() === key);

  return object[result];
}

export function cancelablePromise(task, cancelCallback) {
  let isFinished = false;
  let cancelFunction = () => {
    cancelCallback && cancelCallback();
    isFinished = true;
  }

  const promise = new Promise((resolve, reject) => {
    if (!isFinished) { // may happen if we call cancelFunction() immideately after creation
      return task(resolve, reject, cancelFunction);
    }
  })
    .then((value) => {
      if (!isFinished) {
        isFinished = true;
        return null;
      }
      return value;
    })
    .catch((err) => {
      console.log('Cancelable promise catch: ', err.message);
      isFinished = true;
      return err;
    });

  return { promise, cancelFunction };
}

export function includesParttern(text, pattern) {
  return pattern
    ? text.toLocaleLowerCase().includes(pattern.toLocaleLowerCase())
    : true;
}

export function groupCollection(items, callback) {
  const collection = new Map();
  const get = (key, def) => collection.get(key) ?? collection.set(key, def()).get(key);

  items.forEach(value => callback(value, get));
  return collection.values();
}

export const MergeListsAction = {
  Add: 'add',
  Remove: 'remove',
  Intersect: 'intersect',
  Difference: 'diff',
  Merge: 'merge',
  Replace: 'replace',
  ReplaceAndMerge: 'merge-replace',
};



// !!! Not all are efficient
// TODO: Optimize
const refEqComparator = (a, b) => a === b;

export function mergeLists(first, second, action = MergeListsAction.Add, comparator = refEqComparator) {
  const findItem = (items, item) => items.find(x => comparator(x, item));

  const replaceInFirst = () => first.map(x => findItem(second, x) ?? x);
  const uniqueInFirst = () => first.filter(x => !findItem(second, x));
  const uniqueInSecond = () => second.filter(x => !findItem(first, x));

  switch (action) {
    case MergeListsAction.Add: return [...first, ...second];
    case MergeListsAction.Remove: return uniqueInFirst();
    case MergeListsAction.Difference: return [...uniqueInFirst(), ...uniqueInSecond()];
    case MergeListsAction.Intersect: return first.filter(x => findItem(second, x));
    case MergeListsAction.Merge: return [...first, ...uniqueInSecond()];
    case MergeListsAction.Replace: return replaceInFirst();
    case MergeListsAction.ReplaceAndMerge: return [...replaceInFirst(), ...uniqueInSecond()];
  }
}

export function asArray(value) {
  return Array.isArray(value) ? value : [value];
}

export function asString(value) {
  return typeof (value) === 'string'
    ? value
    : value === undefined || value === null
      ? ''
      : `${value}`;
}

export class DeferredPromise {
  constructor() {
    this.promise = new Promise((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });
  }
}


export function getOrCreate(mapInstance, key, valueFunc) {
  let r = mapInstance.get(key);
  if (r === undefined) {
    r = valueFunc();
    mapInstance.set(key, r);
  }
  return r;
}

export function parseJSON(text, def) {
  try { return JSON.parse(text) }
  catch (error) { }
  return def;
}

export class MultipleErrorsError extends Error {
  constructor(errors, message = 'Multiple errors occured') {
    super(message);
    this.errors = errors;
  }
}

export function isTestStage(stage) {
  return ['test', 'local'].includes(stage);
}

export function splitArrayToChunksByLength(array, chunkLength) {
  return array.reduce((resultArray, item, index) => {
    const chunkIndex = Math.floor(index / chunkLength);

    if (!resultArray[chunkIndex]) {
      resultArray[chunkIndex] = [];
    }

    resultArray[chunkIndex].push(item);

    return resultArray
  }, []);
}

export function splitArrayToChunksBySize(array, maxSize, sizeFun) {
  let currentLength = 0;
  const res = [[]];
  for (const item of array) {
    const size = sizeFun(item);
    if (size > maxSize) {
      return null;
    }

    currentLength += size;
    if (currentLength > maxSize) {
      currentLength = size;
      res.push([]);
    }

    res[res.length - 1].push(item)
  }
  return res
}

export function compareArrayContent(arr1, arr2, equalityFun = (el1, el2) => el1 === el2) {
  if (arr1 === arr2) { return true; }
  if (!arr1 || !arr2) { return false; }
  if (arr1.length !== arr2.length) { return false; }

  const sorted1 = [...arr1].sort(), sorted2 = [...arr2.sort()];
  for (let i = 0; i < sorted1.length; i++) {
    if (!equalityFun(sorted1[i], sorted2[i])) { return false; }
  }
  return true;
}

export function truncateString(str, length) {
  let res = str;
  if (str.length > length) {
    res = str.substring(0, length) + "...";
  }
  return res;
}